import { Transition } from '@headlessui/react';
import { Fragment, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce, isNumber } from 'radash';
import { getClipDisplayLabel } from '@goldcast/player-config/src/utils/clipUtils';
import { AUDIO_CONTROL_POPOVER_TEST_ID } from './AddMenu/constants';
import { useAudioControls } from '@/hooks/useAudioControls';
import RangeSlider from '@/components/atoms/RangeSlider';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useAnalytics from '@/hooks/useAnalytics';
import Icon from '@/components/atoms/Icon';

export const AudioControlsPopup = ({
  setIsOpen,
  isOpen,
  triggerRef
}: {
  setIsOpen: (newVal: boolean) => void;
  isOpen: boolean;
  triggerRef: RefObject<HTMLDivElement>;
}) => {
  const { clipData, clipId } = useClipsContext();
  const { tracks, updateVolume, controlType } = useAudioControls({ isOpen });
  const { trackVolumeChange } = useAnalytics();

  const clipLabel = useMemo(() => {
    return getClipDisplayLabel(clipData);
  }, [clipData]);

  const getDisplayMuteAndVolume = useCallback((track: (typeof tracks)[0]) => {
    const { muted, volume } = track;
    if (muted || (isNumber(volume) && volume === 0)) {
      return { displayMute: true, displayVolume: 0 };
    }
    return { displayMute: muted, displayVolume: volume };
  }, []);

  const trackSliderVolume = useMemo(() => {
    return debounce({ delay: 300 }, ({ volume, muted, label }: { volume: number; muted: boolean; label: string }) => {
      trackVolumeChange({ clipId, clipType: clipLabel, source: 'SLIDER', volume, muted, controlType, label });
    });
  }, [clipLabel, clipId, trackVolumeChange, controlType]);

  const trackVolumeInput = useMemo(() => {
    // larger debounce window - people typing can be slow
    return debounce({ delay: 500 }, ({ volume, muted, label }: { volume: number; muted: boolean; label: string }) => {
      trackVolumeChange({ clipId, clipType: clipLabel, source: 'INPUT', volume, muted, controlType, label });
    });
  }, [clipLabel, clipId, trackVolumeChange, controlType]);

  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const isInsidePopup = menuRef.current && (menuRef.current as HTMLElement)?.contains(event.target as Node);
      const isInsideTrigger = triggerRef.current?.contains(event.target as Node);
      if (isInsidePopup || isInsideTrigger) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    };
  }, [isOpen, setIsOpen, triggerRef]);

  return (
    <Transition
      as={Fragment}
      show={isOpen}
      enter="transition duration-50 ease-out"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="transition duration-50 ease-in"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <div
        id={AUDIO_CONTROL_POPOVER_TEST_ID}
        ref={menuRef}
        className="absolute bottom-12 z-[100] mb-1 mt-2 flex max-h-[312px] w-[416px] flex-col rounded-2xl border bg-white shadow-lg"
      >
        <div className="">
          <div className="p-4">
            <div className="mb-1 flex justify-between">
              <div className="text-base font-medium">Audio Mixer</div>
              <div className="-mt-0.5 rounded-md p-1 hover:bg-slate-100">
                <Icon name="IconX" onClick={() => setIsOpen(false)} size={16} />
              </div>
            </div>
            <div className="text-sm font-normal text-[#6B7280]">
              You can adjust volumes and apply enhancements here.
            </div>
          </div>

          <div className="my-4 flex max-h-[200px] flex-col gap-4 overflow-y-auto px-4">
            {tracks.map((track, index) => {
              const { displayMute, displayVolume } = getDisplayMuteAndVolume(track);
              return (
                <div key={index} className="flex items-center gap-2">
                  <div className={`${track.speakerLabelColor} line-clamp-1 w-32 text-ellipsis text-xs font-medium`}>
                    {track.label}
                  </div>
                  <div
                    className={`rounded-md p-1`}
                    onClick={() => {
                      const newMutedValue = !displayMute;
                      let newVolume = track.volume;
                      if (track.volume === 0) {
                        newVolume = 100;
                      }
                      updateVolume({ videoIds: track.videoIds, volume: newVolume, muted: newMutedValue });

                      trackVolumeChange({
                        clipId,
                        clipType: clipLabel,
                        source: 'MUTE',
                        volume: newVolume,
                        muted: newMutedValue,
                        controlType,
                        label: track.label
                      });
                    }}
                  >
                    <Icon name={displayMute ? 'IconVolumeOff' : 'IconVolume'} size={16} />
                  </div>
                  <div className="w-64">
                    <RangeSlider
                      id={`volume-change-${track.label}-${index}`}
                      scopeSelector={`audio-controller-${index}`}
                      min={0}
                      max={200}
                      value={displayVolume}
                      step={1}
                      onChange={updatedVolume => {
                        const muted = updatedVolume > 0 ? false : true;
                        updateVolume({
                          videoIds: track.videoIds,
                          volume: updatedVolume,
                          muted: muted
                        });
                        trackSliderVolume({ volume: updatedVolume, muted, label: track.label });
                      }}
                      trackHeight="6px"
                      thumbSize="14px"
                    />
                  </div>
                  <div className={classnames('flex items-center rounded-md bg-gray-100 text-xs')}>
                    <input
                      type="number"
                      min={0}
                      max={200}
                      value={displayVolume}
                      onChange={e => {
                        const rawNumber = Number(e.target.value);
                        if (isNaN(rawNumber)) {
                          return;
                        }
                        const updatedVolume = Math.min(Math.max(Math.floor(rawNumber), 0), 200);
                        const muted = updatedVolume > 0 ? false : true;
                        updateVolume({ videoIds: track.videoIds, volume: updatedVolume, muted });
                        trackVolumeInput({ volume: updatedVolume, muted, label: track.label });
                      }}
                      className={classnames(
                        'w-10 rounded-md border-transparent bg-gray-100 p-1 text-end text-xs focus:border-transparent focus:outline-none focus:ring-0'
                      )}
                    />
                    <div className="mr-1.5 text-xs text-[#444444] opacity-30">%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Transition>
  );
};
